/* eslint-disable react-hooks/exhaustive-deps */
import { produce } from 'immer';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import useMount from '../../../../common/hooks/useMount';
import useBreakpoints from '../../../../common/hooks/useBreakpoints';
import { getProductTypeIdBySlug, TaxPayer } from '../../constants';
import { BuyerTypes, FilterTypes } from '../../enums';
import { PriceRange } from '../../models/PriceRange';
import { resources } from '../../resources/resources';
import { filterService } from '../../services/filters';
import { productsService } from '../../services/products';
import ProductListPanel from '../CategoryPage/Components/ProductsListPanel';
import {
  AvailableInFilterState,
  BrandFilterState,
  BuyerTypeFilterState,
  FilterSelectionsState,
  filterSelectionDefaults,
  PriceRangeFilterState,
  ProductListState,
  ProductsOnOfferFilterState,
  TaxationLevelFilterState,
} from '../CategoryPage/types';
import {
  getAvailableInList,
  getBrandList,
  getDiscountedProductsCount,
  getFiltredProducts,
  getPriceRange,
  getSortedProduts,
  getTaxList,
  getUpdatedFilters,
  hasSelectedFilters,
} from '../CategoryPage/utils';
import FilterPanel from '../Common/Filters';
import MobileProductsComponent from '../ProductsPage/ProductsSearched/MobileSearch/MobileSearchResults';

export default function ProductTypePage() {
  const { productTypeSlug } = useParams<{ productTypeSlug: string }>();
  const productTypeId = getProductTypeIdBySlug(productTypeSlug);
  const { t: translate } = useTranslation();
  const { isTablet } = useBreakpoints();
  const navigate = useNavigate();

  const buyerTypeList = useMemo(
    () => [
      {
        buyerType: BuyerTypes.Employee,
        name: translate(resources.filters.buyerType.employee),
      },
      {
        buyerType: BuyerTypes.Dependent,
        name: translate(resources.filters.buyerType.dependent),
      },
    ],
    [translate],
  );

  // #region state
  const [productState, setProductState] = useState<ProductListState>({
    productList: undefined,
    filtredProductList: undefined,
  });
  const [productsOnOfferFilterState, setProductsOnOfferFilterState] =
    useState<ProductsOnOfferFilterState>({
      discountedProductsCount: 0,
    });
  const [priceRangeFilterState, setPriceRangeFilterState] =
    useState<PriceRangeFilterState>({
      priceRange: undefined,
    });
  const [filterSelectionState, setFilterSelectionState] =
    useState<FilterSelectionsState>(filterSelectionDefaults);
  const [currentFilterType, setCurrentFilterType] = useState<number>();

  const [availableInFilterState, setAvailableInFilterState] =
    useState<AvailableInFilterState>({
      availableInList: undefined,
      filteredAvailableInList: undefined,
    });
  const [taxationLevelFilterState, setTaxationLevelFilterState] =
    useState<TaxationLevelFilterState>({
      taxationLevelList: undefined,
      filteredTaxationLevelList: undefined,
    });
  const [
    employerTaxationLevelFilterState,
    setEmployerTaxationLevelFilterState,
  ] = useState<TaxationLevelFilterState>({
    taxationLevelList: undefined,
    filteredTaxationLevelList: undefined,
  });
  const [brandFilterState, setBrandFilterState] = useState<BrandFilterState>({
    brandList: undefined,
    filteredBrandList: undefined,
  });
  const [buyerTypeFilterState, setBuyerTypeFilterState] =
    useState<BuyerTypeFilterState>({
      buyerTypeList,
      filteredBuyerTypeList: buyerTypeList,
    });
  // #endregion

  const categoriesIds = useMemo(
    () => _.uniq(productState.productList?.map(p => p.categoryId)),
    [productState.productList],
  );

  const hasFilters =
    productState.productList?.length !==
    productState.filtredProductList?.length;

  // #region data loaders
  const loadProducts = async (abortController?: AbortController) => {
    if (productTypeId === undefined) return;

    setProductState({ productList: undefined, filtredProductList: undefined });
    setProductsOnOfferFilterState({ discountedProductsCount: 0 });
    setPriceRangeFilterState({ priceRange: undefined });

    const result = await productsService.getProductsForProductType(
      productTypeId,
      { signal: abortController?.signal },
    );

    if (!result.hasErrors) {
      if (result.data.length > 0) {
        setProductState({
          productList: result.data,
          filtredProductList: result.data,
        });
        setProductsOnOfferFilterState({
          discountedProductsCount: getDiscountedProductsCount(result.data),
        });
        setPriceRangeFilterState({ priceRange: getPriceRange(result.data) });
      } else {
        navigate('/not-found');
      }
    }
  };

  const loadBrandList = async () => {
    setBrandFilterState({ brandList: undefined, filteredBrandList: undefined });

    const result =
      await filterService.getBrandFilterForCategories(categoriesIds);
    if (!result.hasErrors) {
      setBrandFilterState({
        brandList: result.data,
        filteredBrandList: getBrandList(
          productState.productList ?? [],
          result.data,
        ),
      });
    }
  };

  const loadTaxationLevelList = async () => {
    setTaxationLevelFilterState({
      taxationLevelList: undefined,
      filteredTaxationLevelList: undefined,
    });
    const result = await filterService.getTaxFilterForCategories(categoriesIds);
    if (!result.hasErrors) {
      const employeeTaxationLevels = result.data.filter(
        t => t.payer === TaxPayer.Employee,
      );
      const employerTaxationLevels = result.data.filter(
        t => t.payer === TaxPayer.Employer,
      );
      setTaxationLevelFilterState({
        taxationLevelList: employeeTaxationLevels,
        filteredTaxationLevelList: getTaxList(
          productState.productList ?? [],
          employeeTaxationLevels,
        ),
      });
      setEmployerTaxationLevelFilterState({
        taxationLevelList: employerTaxationLevels,
        filteredTaxationLevelList: getTaxList(
          productState.productList ?? [],
          employerTaxationLevels,
        ),
      });
    }
  };

  const loadAvailableInList = async () => {
    setAvailableInFilterState({
      availableInList: undefined,
      filteredAvailableInList: undefined,
    });
    const result =
      await filterService.getCountyFilterForCategories(categoriesIds);
    if (!result.hasErrors) {
      setAvailableInFilterState({
        availableInList: result.data,
        filteredAvailableInList: getAvailableInList(
          productState.productList ?? [],
          result.data,
        ),
      });
    }
  };
  // #endregion

  // #region filter change handlers
  const handlePriceRangeFilterChanged = useCallback((range: PriceRange) => {
    setFilterSelectionState(
      produce(draft => {
        draft.priceRange = range;
      }),
    );
    setCurrentFilterType(FilterTypes.PriceRange);
  }, []);

  const handleAvailableInFilterChanged = useCallback((countyIds: number[]) => {
    setFilterSelectionState(
      produce(draft => {
        draft.selectedCountyIds = countyIds;
      }),
    );
    setCurrentFilterType(FilterTypes.AvailableIn);
  }, []);

  const handleBrandFilterChanged = useCallback((brandIds: number[]) => {
    setFilterSelectionState(
      produce(draft => {
        draft.selectedBrandIds = brandIds;
      }),
    );
    setCurrentFilterType(FilterTypes.Brand);
  }, []);

  const handleBuyerTypeFilterChanged = useCallback(
    (buyerTypes: BuyerTypes[]) => {
      setFilterSelectionState(
        produce(draft => {
          draft.selectedBuyerTypes = buyerTypes;
        }),
      );
      setCurrentFilterType(FilterTypes.BuyerType);
    },
    [],
  );

  const handleTaxFilterChanged = useCallback((taxClassIds: number[]) => {
    setFilterSelectionState(
      produce(draft => {
        draft.selectedTaxClassIds = taxClassIds;
      }),
    );
    setCurrentFilterType(FilterTypes.TaxationLevel);
  }, []);

  const handleEmployerTaxFilterChanged = useCallback(
    (taxClassIds: number[]) => {
      setFilterSelectionState(
        produce(draft => {
          draft.selectedEmployerTaxClassIds = taxClassIds;
        }),
      );
      setCurrentFilterType(FilterTypes.EmployerTaxationLevel);
    },
    [],
  );

  const handleProductsOnOfferFilterChanged = useCallback(
    (isSelected: boolean) => {
      setFilterSelectionState(
        produce(draft => {
          draft.showDiscountedProducts = isSelected;
        }),
      );
      setCurrentFilterType(FilterTypes.ShowDiscountedProducts);
    },
    [],
  );

  const handleSortingOrderChanged = (id: number) => {
    setProductState(
      produce(draft => {
        draft.filtredProductList = getSortedProduts[id](
          draft.filtredProductList,
        );
      }),
    );
  };

  const handleClearFilters = () => {
    setFilterSelectionState(filterSelectionDefaults);

    if (productState.productList) {
      setPriceRangeFilterState({
        priceRange: getPriceRange(productState.productList),
      });
    }

    setProductState(
      produce(d => {
        d.filtredProductList = d.productList;
      }),
    );
  };
  //#endregion

  useMount(
    abortController => {
      loadProducts(abortController);
    },
    [productTypeId],
  );

  useEffect(() => {
    if (categoriesIds?.length > 0) {
      loadBrandList();
      loadTaxationLevelList();
      loadAvailableInList();
    }
  }, [productState.productList, categoriesIds]);

  useEffect(() => {
    setProductState(
      produce(d => {
        d.filtredProductList = getFiltredProducts(
          d.productList,
          filterSelectionState,
        );
      }),
    );
  }, [
    filterSelectionState.selectedBrandIds,
    filterSelectionState.selectedBuyerTypes,
    filterSelectionState.selectedCountyIds,
    filterSelectionState.selectedTaxClassIds,
    filterSelectionState.showDiscountedProducts,
    filterSelectionState.priceRange,
  ]);

  useEffect(() => {
    const filters = getUpdatedFilters(
      productState.productList,
      productState.filtredProductList,
      availableInFilterState.availableInList,
      brandFilterState.brandList,
      taxationLevelFilterState.taxationLevelList,
      employerTaxationLevelFilterState.taxationLevelList,
      buyerTypeFilterState.buyerTypeList,
      undefined,
      currentFilterType === FilterTypes.PriceRange,
      hasSelectedFilters(filterSelectionState),
    );
    if (!filters) return;

    if (currentFilterType !== FilterTypes.AvailableIn)
      setAvailableInFilterState(
        produce(d => {
          d.filteredAvailableInList = filters.filteredCounties;
        }),
      );
    if (currentFilterType !== FilterTypes.TaxationLevel)
      setTaxationLevelFilterState(
        produce(d => {
          d.filteredTaxationLevelList = filters.filteredTax;
        }),
      );
    if (currentFilterType !== FilterTypes.Brand)
      setBrandFilterState(
        produce(d => {
          d.filteredBrandList = filters.filteredBrands;
        }),
      );
    if (currentFilterType !== FilterTypes.BuyerType)
      setBuyerTypeFilterState(
        produce(d => {
          d.filteredBuyerTypeList = filters.filteredBuyerTypes;
        }),
      );
    if (currentFilterType !== FilterTypes.PriceRange)
      setPriceRangeFilterState({ priceRange: filters.priceRange });
    if (currentFilterType !== FilterTypes.ShowDiscountedProducts)
      setProductsOnOfferFilterState({
        discountedProductsCount: filters.discountedProductsCount,
      });
  }, [productState.filtredProductList]);

  if (productTypeId === undefined) {
    return <Navigate to="/not-found" replace />;
  }

  if (isTablet) {
    return (
      <MobileProductsComponent
        filteredProducts={productState.filtredProductList}
        handleSortingOrderChange={handleSortingOrderChanged}
        filters={{
          filteredAvailableInList:
            availableInFilterState.filteredAvailableInList,
          filteredBrandList: brandFilterState.filteredBrandList,
          filteredBuyerTypeList: buyerTypeFilterState.filteredBuyerTypeList,
          filteredTaxationLevelList:
            taxationLevelFilterState.filteredTaxationLevelList,
          filteredEmployerTaxationLevelList:
            employerTaxationLevelFilterState.filteredTaxationLevelList,
          priceRange: priceRangeFilterState.priceRange,
          discountedProductsCount:
            productsOnOfferFilterState.discountedProductsCount,
          selectionFilterState: filterSelectionState,
          hasActiveFilters: hasFilters,
          priceRangeFilterChanged: handlePriceRangeFilterChanged,
          availableInFilterChanged: handleAvailableInFilterChanged,
          brandFilterChanged: handleBrandFilterChanged,
          buyerTypeFilterChanged: handleBuyerTypeFilterChanged,
          taxFilterChanged: handleTaxFilterChanged,
          employerTaxFilterChanged: handleEmployerTaxFilterChanged,
          productsOnOfferFilterChanged: handleProductsOnOfferFilterChanged,
          clearFilters: handleClearFilters,
        }}
        isTabletView={isTablet}
        isDiscountVoucherList={false}
        isBenefitBucksList={false}
      />
    );
  }

  return (
    <section className="section-main section-actions category-page">
      <div className="container">
        <div className="area-actions">
          <FilterPanel
            availableInList={availableInFilterState.filteredAvailableInList}
            brandList={brandFilterState.filteredBrandList}
            buyerTypeList={buyerTypeFilterState.filteredBuyerTypeList}
            taxationLevelList={
              taxationLevelFilterState.filteredTaxationLevelList
            }
            employerTaxationLevelList={
              employerTaxationLevelFilterState.filteredTaxationLevelList
            }
            priceRange={priceRangeFilterState.priceRange}
            discountedProdutsCount={
              productsOnOfferFilterState.discountedProductsCount
            }
            prevSelectionState={filterSelectionState}
            hasActiveFilters={hasFilters}
            handlePriceRangeFilterChanged={handlePriceRangeFilterChanged}
            handleAvailableInFilterChanged={handleAvailableInFilterChanged}
            handleBrandFilterChanged={handleBrandFilterChanged}
            handleBuyerTypeFilterChanged={handleBuyerTypeFilterChanged}
            handleTaxFilterChanged={handleTaxFilterChanged}
            handleEmployerTaxFilterChanged={handleEmployerTaxFilterChanged}
            handleProductsOnOfferFilterChanged={
              handleProductsOnOfferFilterChanged
            }
            handleClearFilters={handleClearFilters}
            filteredProducts={productState.filtredProductList}
          />
        </div>
        <div className="area-content">
          <ProductListPanel
            productList={productState.filtredProductList}
            isDiscountVoucherList={false}
            isBenefitBucksList={false}
            handleSortingOrderChanged={handleSortingOrderChanged}
          />
        </div>
      </div>
    </section>
  );
}
